<template>
  <aside class="feed-desktop-aside">
    <button v-if="user" type="button" class="feed-widget-card feed-widget-card--interactive" @click="goToProfile">
      <div class="feed-widget-card__profile">
        <user-avatar :user="user" size="14" class="feed-widget-card__avatar"/>
        <div class="feed-widget-card__profile-copy">
          <div class="feed-widget-card__title">{{ user.name }}</div>
          <div class="feed-widget-card__subtitle">
            <span v-if="user.id_verified" class="feed-widget-card__badge">Verified</span>
            <span v-else>Complete your profile</span>
          </div>
        </div>
      </div>
      <div v-if="profileStats.length" class="feed-widget-card__stats">
        <div v-for="stat in profileStats" :key="stat.label" class="feed-widget-card__stat">
          <span class="feed-widget-card__stat-value">{{ stat.value }}</span>
          <span class="feed-widget-card__stat-label">{{ stat.label }}</span>
        </div>
      </div>
      <span class="feed-widget-card__cta">View profile</span>
    </button>

    <div class="feed-widget-card">
      <div class="feed-widget-card__title">Discover</div>
      <p class="feed-widget-card__body">
        Browse events, search members, and connect with people in the community.
      </p>
      <div class="feed-widget-card__links">
        <button type="button" class="feed-widget-link" @click="goTo('Events')">Events</button>
        <button type="button" class="feed-widget-link" @click="goTo('Search')">Search</button>
        <button type="button" class="feed-widget-link" @click="goTo('Flirt')">Chat</button>
      </div>
    </div>

    <div class="feed-widget-card feed-widget-card--muted">
      <div class="feed-widget-card__title">Upcoming events</div>
      <div v-if="eventsLoading" class="feed-widget-card__body">Loading events…</div>
      <div v-else-if="!upcomingEvents.length" class="feed-widget-card__body">
        No upcoming events right now. Check back soon or host your own.
      </div>
      <div v-else class="feed-widget-card__event-list">
        <button
            v-for="event in upcomingEvents"
            :key="event.id"
            type="button"
            class="feed-widget-event"
            @click="viewEvent(event)">
          <div
              v-if="event.photo_url"
              :style="{ backgroundImage: `url('${event.photo_url}')` }"
              class="feed-widget-event__thumb"/>
          <div v-else class="feed-widget-event__thumb feed-widget-event__thumb--empty">
            <svg class="h-4 fill-current text-brand-light-gray/30" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 512">
              <path d="M159.2 12.6c5.7-10.5 18.1-15.2 29.3-11.1L320 49.3 451.5 1.5c11.2-4.1 23.7 .7 29.3 11.1l81.6 151c32.4 60.1 10.6 133.3-45.6 167L552.6 429l55.2-20.1c12.5-4.5 26.2 1.9 30.8 14.3s-1.9 26.2-14.3 30.8l-77.8 28.3-77.8 28.3c-12.5 4.5-26.2-1.9-30.8-14.3s1.9-26.2 14.3-30.8l55.2-20.1-35.8-98.5c-61.2 9.4-121.7-29.6-136.3-92.5L320 188.6l-15.3 65.8c-14.6 62.9-75.1 101.9-136.3 92.5l-35.8 98.5 55.2 20.1c12.5 4.5 18.9 18.3 14.3 30.8s-18.3 18.9-30.8 14.3L93.6 482.3 15.8 454C3.3 449.4-3.1 435.7 1.5 423.2s18.3-18.9 30.8-14.3L87.4 429l35.8-98.4c-56.2-33.7-78.1-106.9-45.6-167l81.6-151zm32.4 41L156.8 118 277 161.7l16.5-71L191.6 53.6z"/>
            </svg>
          </div>
          <div class="feed-widget-event__copy">
            <span class="feed-widget-event__name">{{ event.name }}</span>
            <span class="feed-widget-event__meta">{{ formatEventDate(event) }}</span>
            <span v-if="event.location" class="feed-widget-event__location">{{ event.location }}</span>
          </div>
        </button>
      </div>
      <button type="button" class="feed-widget-link feed-widget-link--inline" @click="goTo('Events')">
        Browse all events
      </button>
    </div>

    <div class="feed-widget-card feed-widget-card--muted">
      <div class="feed-widget-card__title">Feed tips</div>
      <p class="feed-widget-card__body">
        Use feed preferences to fine-tune what appears in For You, and toggle NSFW reveal when you are subscribed.
      </p>
      <button type="button" class="feed-widget-link feed-widget-link--inline" @click="goToFeedPreferences">
        Open feed preferences
      </button>
    </div>
  </aside>
</template>

<script setup lang="ts">
import {computed, inject, onMounted, Ref, ref} from 'vue';
import {useIonRouter} from '@ionic/vue';
import {DateTime} from 'luxon';
import UserAvatar from '@/components/UserAvatar.vue';
import ApiService from '@/services/api/apiService';
import {EventInterface} from '@/interfaces/EventInterface';
import {UserInterface} from '@/interfaces/UserInterface';

const ionRouter = useIonRouter();
const user = inject<Ref<UserInterface | undefined>>('user');

const upcomingEvents = ref<EventInterface[]>([]);
const eventsLoading = ref(true);

const profileStats = computed(() => {
  if (!user?.value) return [];
  return [
    {label: 'Friends', value: user.value.friends ?? 0},
    {label: 'Posts', value: user.value.posts ?? 0},
    {label: 'Followers', value: user.value.followers ?? 0},
  ];
});

const pickRandomEvents = (events: EventInterface[], count = 3) => {
  const pool = [...events];
  for (let i = pool.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [pool[i], pool[j]] = [pool[j], pool[i]];
  }
  return pool.slice(0, count);
};

const loadUpcomingEvents = async () => {
  eventsLoading.value = true;
  try {
    const response = await ApiService.Events().paginate(1);
    upcomingEvents.value = pickRandomEvents(response.data ?? [], 3);
  } catch {
    upcomingEvents.value = [];
  } finally {
    eventsLoading.value = false;
  }
};

onMounted(loadUpcomingEvents);

const formatEventDate = (event: EventInterface) => {
  const raw = event?.starts_at;
  if (!raw) return '';
  const dt = DateTime.fromISO(String(raw));
  return dt.isValid ? dt.toFormat("EEE d MMM · HH:mm") : '';
};

const goTo = (name: string) => {
  ionRouter.navigate({name}, 'forward', 'push');
};

const goToProfile = () => {
  goTo('Profile');
};

const goToFeedPreferences = () => {
  goTo('PineappleFeedPreferences');
};

const viewEvent = (event: EventInterface) => {
  ionRouter.navigate({
    name: 'Event',
    params: {
      eventId: event.id,
    },
  }, 'forward', 'push');
};
</script>
